import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, LayoutGrid, Loader2, Bell, Sun, Moon, SunMoon, Wifi, WifiOff, User, Settings, LogOut, CalendarClock } from 'lucide-react';
import { Button } from '../components/ui/button';
import KanbanBoard from '../components/kanban/KanbanBoard';
import { authService } from '../services/authService';
import api from '../services/api';
import socketService from '../services/socketService';
import { instanciaService } from '../services/instanciaService';
import { useTheme, Theme } from '../contexts/ThemeContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { PerfilModal } from '../components/PerfilModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '../components/ui/dropdown-menu';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001')?.replace(/\/api$/, '');

interface Instance {
  id: string;
  nome: string;
  statusConexao: string;
  ultimaConexao?: string;
}

interface Notificacao {
  id: string;
  conteudo: string;
  tipo: string;
  lida: boolean;
  ticketId?: string;
  mensagemId?: string;
  dataCriacao: string;
}

const KanbanPage = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const { theme, setTheme } = useTheme();
  const [permitirKanban, setPermitirKanban] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  // Header state
  const [instances, setInstances] = useState<Instance[]>([]);
  const [instance, setInstance] = useState<Instance | null>(null);
  const [instancesLoading, setInstancesLoading] = useState(true);
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const [openChange, setOpenChange] = useState(false);
  const [pollingActive, setPollingActive] = useState(true);

  const notificacoesNaoLidas = notificacoes.filter(n => !n.lida).length;

  const getInitials = (name: string | null | undefined = '') => {
    return (name ?? '')
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';
  };

  const fetchInstances = useCallback(async () => {
    try {
      setInstancesLoading(true);
      const response: any = await instanciaService.listar();
      if (response.data.instancias && response.data.instancias.length > 0) {
        const instancesData = response.data.instancias.map((item: any) => ({
          id: item.id,
          nome: item.nome || item.instanceName || 'Instância sem nome',
          statusConexao: item.statusConexao || 'disconnected',
          ultimaConexao: item.ultimaConexao,
        }));
        setInstances(instancesData);
        const savedInstanceId = localStorage.getItem('selectedInstanceId');
        const defaultInstance = savedInstanceId
          ? instancesData.find((i: Instance) => i.id === savedInstanceId) || instancesData[0]
          : instancesData[0];
        setInstance(defaultInstance);
      }
    } catch {
      // silent
    } finally {
      setInstancesLoading(false);
    }
  }, []);

  const fetchInstanceStatus = useCallback(async (instanceId: string) => {
    try {
      const response: any = await instanciaService.verificarStatus(instanceId);
      if (response?.status === 429) {
        setPollingActive(false);
        return;
      }
      const newStatus = response.data.statusConexao || response.data.status || 'disconnected';
      setInstances(prev => prev.map(i => i.id === instanceId ? { ...i, statusConexao: newStatus, ultimaConexao: response.data.ultimaConexao || i.ultimaConexao } : i));
      setInstance(prev => prev?.id === instanceId ? { ...prev, statusConexao: newStatus, ultimaConexao: response.data.ultimaConexao || prev.ultimaConexao } : prev);
    } catch {
      // silent
    }
  }, []);

  const handleInstanceSelect = useCallback((selectedInstance: Instance) => {
    setInstance(selectedInstance);
    localStorage.setItem('selectedInstanceId', selectedInstance.id);
    fetchInstanceStatus(selectedInstance.id);
  }, [fetchInstanceStatus]);

  useEffect(() => { fetchInstances(); }, [fetchInstances]);

  useEffect(() => {
    if (instances.length > 0 && pollingActive) {
      const interval = setInterval(() => {
        instances.forEach(i => fetchInstanceStatus(i.id));
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [instances, fetchInstanceStatus, pollingActive]);

  // Notifications
  const fetchNotificacoes = useCallback(async () => {
    try {
      if (user?.id) {
        const response = await api.get('/notificacoes');
        setNotificacoes(response.data.data || []);
      }
    } catch { /* silent */ }
  }, [user?.id]);

  useEffect(() => { fetchNotificacoes(); }, [fetchNotificacoes]);

  useEffect(() => {
    const socket = socketService.getSocket();
    if (socket) {
      const handleNovaNotificacao = (notificacao: Notificacao) => {
        setNotificacoes(prev => [notificacao, ...prev]);
      };
      socket.on('nova_notificacao', handleNovaNotificacao);
      return () => { socket.off('nova_notificacao', handleNovaNotificacao); };
    }
  }, []);

  const handleMarcarComoLida = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.put(`/notificacoes/${id}/ler`);
      setNotificacoes(prev => prev.map(n => n.id === id ? { ...n, lida: true } : n));
    } catch { /* silent */ }
  };

  const handleMarcarTodasComoLida = async () => {
    try {
      await api.put('/notificacoes/ler-todas');
      setNotificacoes(prev => prev.map(n => ({ ...n, lida: true })));
    } catch { /* silent */ }
  };

  const handleLogout = () => {
    try { socketService.disconnect(); } catch { /* ignore */ }
    authService.logout();
    window.location.href = '/login';
  };

  // Permission check
  useEffect(() => {
    const checkConfig = async () => {
      try {
        const isPrivileged = user?.role === 'SuperAdmin' || user?.role === 'Admin';
        const empresaId = isPrivileged ? undefined : user?.empresaId;
        if (!empresaId) {
          setPermitirKanban(true);
          setLoading(false);
          return;
        }
        const response = await api.get(`/configuracoes-chat/empresa/${empresaId}`);
        const config = response?.data?.configuracao;
        setPermitirKanban(config?.permitirKanban ?? false);
      } catch {
        setPermitirKanban(false);
      } finally {
        setLoading(false);
      }
    };
    checkConfig();
  }, [user?.role, user?.empresaId]);

  useEffect(() => {
    if (!loading && permitirKanban === false) {
      navigate('/dashboard');
    }
  }, [loading, permitirKanban, navigate]);

  if (loading) {
    return (
      <div className="flex flex-col h-[100dvh] bg-gray-100 dark:bg-gray-900 items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!permitirKanban) return null;

  return (
    <div className="flex flex-col h-[100dvh] bg-gray-100 dark:bg-gray-900 overflow-hidden">
      <header className="h-14 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 flex items-center px-4 gap-3 flex-shrink-0 z-10">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/dashboard')}
          className="h-8 w-8 text-gray-600 dark:text-gray-300"
        >
          <ArrowLeft size={18} />
        </Button>
        <LayoutGrid size={20} className="text-purple-600 dark:text-purple-400" />
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Kanban</h1>

        <div className="ml-auto flex items-center text-gray-600 dark:text-gray-400 gap-4">
          {/* Theme toggle */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full dark:text-gray-500">
                {theme === 'light' ? <Sun size={18} /> : theme === 'dark' ? <Moon size={18} /> : <SunMoon size={18} />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="dark:bg-slate-800">
              <DropdownMenuRadioGroup value={theme} onValueChange={(value) => setTheme(value as Theme)}>
                <DropdownMenuRadioItem value="light" className="cursor-pointer dark:text-gray-500">
                  <Sun className="mr-2 h-4 w-4" />Claro
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="dark" className="cursor-pointer dark:text-gray-500">
                  <Moon className="mr-2 h-4 w-4" />Escuro
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="system" className="cursor-pointer dark:text-gray-500">
                  <SunMoon className="mr-2 h-4 w-4" />Sistema
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Instance status */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full w-auto p-2" title={instance?.statusConexao === 'connected' ? 'Instância conectada' : 'Instância desconectada'}>
                {instance?.statusConexao === 'connected' ? <Wifi size={20} className="text-green-600" /> : <WifiOff size={20} className="text-orange-600" />}
                {instances.find(i => i.statusConexao !== 'connected') && (
                  <div className="h-2 w-2 bg-red-500 rounded-full animate-ping" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="dark:bg-slate-800">
              <DropdownMenuLabel className="dark:text-gray-500">Instâncias</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {instancesLoading ? (
                <div className="px-2 py-1.5 text-sm dark:text-gray-500">Carregando...</div>
              ) : instances.length > 0 ? (
                instances.map(i => (
                  <DropdownMenuItem key={i.id} className="flex items-center gap-2" onClick={() => handleInstanceSelect(i)}>
                    {i.statusConexao === 'connected' ? <Wifi size={16} className="text-green-600" /> : <WifiOff size={16} className="text-orange-600" />}
                    <div className="flex-1">
                      <div className="font-medium text-sm">{i.nome}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {i.statusConexao === 'connected' ? 'Conectado' : 'Desconectado'}
                        {i.ultimaConexao && ` • ${new Date(i.ultimaConexao).toLocaleString()}`}
                      </div>
                    </div>
                  </DropdownMenuItem>
                ))
              ) : (
                <div className="px-2 py-1.5 text-sm text-gray-500">Nenhuma instância</div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Notifications */}
          {user?.role !== 'SuperAdmin' && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full dark:bg-slate-800" title="Notificações">
                  <Bell size={20} />
                  {notificacoesNaoLidas > 0 && (
                    <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-red-500 text-[10px] text-white flex items-center justify-center">
                      {notificacoesNaoLidas}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="dark:bg-slate-800 w-80 max-h-96 overflow-y-auto">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <DropdownMenuLabel className="dark:text-gray-500">Notificações</DropdownMenuLabel>
                  {notificacoesNaoLidas > 0 && (
                    <Button variant="ghost" size="sm" className="h-auto p-0 text-xs text-blue-500 hover:text-blue-700 hover:bg-transparent" onClick={handleMarcarTodasComoLida}>
                      Marcar todas como lidas
                    </Button>
                  )}
                </div>
                <DropdownMenuSeparator />
                {notificacoes.length > 0 ? (
                  notificacoes.map(n => (
                    <DropdownMenuItem key={n.id} className={`flex flex-col items-start gap-1 p-3 cursor-pointer ${!n.lida ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`} onClick={() => n.ticketId && navigate(`/dashboard?ticketId=${n.ticketId}${n.mensagemId ? `&mensagemId=${n.mensagemId}` : ''}`)}>
                      <div className="flex justify-between w-full">
                        <span className={`text-sm ${!n.lida ? 'font-semibold' : ''}`}>{n.conteudo}</span>
                      </div>
                      <div className="flex justify-between w-full items-center mt-1">
                        <span className="text-xs text-gray-500">{new Date(n.dataCriacao).toLocaleString()}</span>
                        {!n.lida && (
                          <Button variant="ghost" size="sm" className="h-6 text-xs text-blue-500" onClick={(e) => handleMarcarComoLida(n.id, e)}>
                            Marcar como lida
                          </Button>
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))
                ) : (
                  <DropdownMenuItem disabled className="text-xs text-gray-500 italic">
                    Nenhuma notificação nova
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Profile */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="hover:bg-gray-200 dark:hover:bg-slate-700 rounded-md dark:bg-slate-800 flex items-center gap-2 py-2 px-4">
                <div className="flex flex-col items-start mr-2">
                  <span className="text-xs font-medium text-gray-800 dark:text-gray-200">{user?.nome}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{user?.role}</span>
                </div>
                <Avatar className="h-8 w-8 cursor-pointer dark:bg-slate-800">
                  <AvatarImage
                    src={user?.avatarUrl ? `${API_BASE_URL}${user.avatarUrl.startsWith('/') ? '' : '/'}${user.avatarUrl}` : undefined}
                    alt={user?.nome}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (user?.avatarUrl && !user.avatarUrl.startsWith('http')) {
                        target.src = `${API_BASE_URL}${user.avatarUrl.startsWith('/') ? '' : '/'}${user.avatarUrl}`;
                      } else {
                        target.style.display = 'none';
                      }
                    }}
                  />
                  <AvatarFallback className="bg-gray-400 text-white text-sm">{getInitials(user?.nome)}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="dark:bg-slate-800">
              <div onClick={() => setOpenChange(true)} className="cursor-pointer flex items-center p-1 bg-gray-200 dark:bg-slate-700 dark:text-white rounded">
                <Avatar className="h-8 w-8">
                  <AvatarImage
                    src={user?.avatarUrl ? `${API_BASE_URL}${user.avatarUrl.startsWith('/') ? '' : '/'}${user.avatarUrl}` : undefined}
                    alt={user?.nome}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (user?.avatarUrl && !user.avatarUrl.startsWith('http')) {
                        target.src = `${API_BASE_URL}${user.avatarUrl.startsWith('/') ? '' : '/'}${user.avatarUrl}`;
                      } else {
                        target.style.display = 'none';
                      }
                    }}
                  />
                  <AvatarFallback className="bg-gray-400 text-white text-xs dark:text-white">{getInitials(user?.nome)}</AvatarFallback>
                </Avatar>
                <DropdownMenuLabel className="dark:text-gray-500 text-xs dark:text-white">{user?.nome}</DropdownMenuLabel>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="dark:text-gray-500">Opções</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer" onClick={() => setOpenChange(true)}>
                <User className="mr-2 h-4 w-4" />
                <span>Perfil</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/disparo-automatico')}>
                <CalendarClock className="mr-2 h-4 w-4" />
                <span>Disparo Automático</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer" onClick={() => window.location.href = '/configuracoes'}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Configurações</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer" onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sair</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <PerfilModal isOpen={openChange} onOpenChange={setOpenChange} />
        </div>
      </header>

      <KanbanBoard className="flex-1 overflow-hidden" />
    </div>
  );
};

export default KanbanPage;
